import type { MutableValue, ObservableValue } from '@amazon/vinyl-observable'
import { data, externalData } from '@amazon/vinyl-observable'
import type { LogTarget, Unsubscribe } from '@amazon/vinyl-util'
import { logDebug, logError, logInfo, LruCache, requestWithRetry } from '@amazon/vinyl-util'
import type { MessageValue } from './format'

// ---------------------------------------------------------------------------
// Current locale
// ---------------------------------------------------------------------------

const target: LogTarget = {
    logPrefix: 'i18n',
}

const currentLocale: MutableValue<string> = data('und')

export function setLocale(locale: string): void {
    logInfo(target, 'locale changed to:', locale)
    currentLocale.value = locale
}

export function getLocale(): ObservableValue<string> {
    return currentLocale
}

export function resolveLocale(locale: string): string {
    return locale === 'und' ? i18nConfig.sourceLocale : locale
}

export function getBrowserLocale(): string {
    return typeof navigator !== 'undefined' ? navigator.language : 'und'
}

const browserLocale = externalData(getBrowserLocale(), (setData) => {
    if (typeof window === 'undefined') return () => {}
    const update = () => setData(getBrowserLocale())
    window.addEventListener('languagechange', update)
    return () => window.removeEventListener('languagechange', update)
})

export function bindBrowserLocale(): Unsubscribe {
    return browserLocale.onData(setLocale)
}

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

export interface I18nConfig {
    readonly translationPath: string
    readonly sourceLocale: string
}

let i18nConfig: I18nConfig = {
    translationPath: './i18n/{bundle}/{locale}.json',
    sourceLocale: 'en-US',
}

export function setI18nConfig(partial: Partial<I18nConfig>): void {
    i18nConfig = { ...i18nConfig, ...partial }
    clearBundleCache()
}

export function getI18nConfig(): I18nConfig {
    return i18nConfig
}

// ---------------------------------------------------------------------------
// Bundle loading (cached, evicts on failure)
// ---------------------------------------------------------------------------

const cache = new LruCache<string, Promise<Record<string, MessageValue>>>(32)

export function loadBundle(bundle: string, locale: string): Promise<Record<string, MessageValue>> {
    const key = `${bundle}:${locale}`
    const cached = cache.get(key)
    if (cached) return cached

    const url = i18nConfig.translationPath.replace('{locale}', locale).replace('{bundle}', bundle)
    logDebug(target, `loadBundle, key=${key}, url=${url}`)
    const promise = requestWithRetry(url)
        .then((r) => r.json() as Promise<Record<string, MessageValue>>)
        .catch((e) => {
            logError(target, `Could not load ${key} at ${url}`, e)
            cache.delete(key)
            throw e
        })

    cache.set(key, promise)
    return promise
}

export function clearBundleCache(): void {
    cache.clear()
}
